import { Action } from '../../types/actions';
import { Channel, BLAdmin } from '../../types/working-model';
import {
	SIGN_IN,
	UPDATE_PASSWORD,
	FORGOT_PASSWORD,
	REFRESH_TOKEN,
	SIGN_OUT,
	GET_ADMIN_CHANNELS,
	SET_ADMIN_CHANNEL,
	UPDATE_CHANNEL,
	CONFIRM_CODE,
	SET_TEMP_NUMBER,
	ADD_CHECKLIST_ITEM,
	CLEAR_CHECKLIST,
	COMPLETE_WELCOME_ONBOARDING,
	UPDATE_ADMIN_PROFILE
} from '../actions/authentication';
import { handle } from 'redux-pack';
import { jwtDecode, handleLoginErrorMessage } from '../../utils/utils';
import { getStorageItem, removeStorageItem, setStorageItem } from '../../utils/local-storage';

export enum StoredTokens {
	currentUser = "currentUser",
	storedTwoFactor = "storedTwoFactor",
	currentChannel = "currentChannel"
}

const initialToken = getStorageItem(StoredTokens.currentUser) ?? null;
const initialTwoFactor = getStorageItem(StoredTokens.storedTwoFactor) ?? null;

export interface AuthState {
	token: string | null;
	user: BLAdmin | null;
	channels: Channel[];
	signinIn: boolean;
	signInFailure: string;
	forgotPasswordLoading: boolean;
	forgotPasswordError: string;
	forgotPasswordSuccess: boolean;
	updatePasswordLoading: boolean;
	updatePasswordError: string;
	updatePasswordSuccess: boolean;
	temp_profile: string | null;
	two_factor_stored: string | null;
	phone_hash: string | null;
	request_phone: boolean;
	onboardedLoading: boolean;
	awaitCode: boolean;
	changingChannels: boolean;
	updateChannelError: boolean;
}

const initialState: AuthState = {
	token: initialToken,
	user: initialToken ? jwtDecode(initialToken) as BLAdmin : null,
	channels: [],
	signinIn: false,
	signInFailure: '',
	forgotPasswordLoading: false,
	forgotPasswordError: '',
	forgotPasswordSuccess: false,
	updatePasswordLoading: false,
	updatePasswordError: '',
	updatePasswordSuccess: false,
	temp_profile: null,
	two_factor_stored: initialTwoFactor,
	phone_hash: null,
	request_phone: false,
	onboardedLoading: false,
	awaitCode: false,
	changingChannels: false,
	updateChannelError: false
};

const setStorageAndGetUser = (token: string): BLAdmin => {
	//set token in localStorage
	setStorageItem(StoredTokens.currentUser, token, 1);

	//decode JWT
	const user = jwtDecode(token) as BLAdmin;

	//use plain localStorage for this key so it doesn't have to be parsed by the useChannelMonitor hook
	localStorage.setItem(StoredTokens.currentChannel, String(user.active_channel));

	return user;
};

export default function AuthReducer(
	state: AuthState = initialState,
	action: Action
): AuthState {
	switch (action.type) {
		case REFRESH_TOKEN: {
			return handle(state, action, {
				success: (state) => {
					if (action.payload.token) {
						const user = setStorageAndGetUser(action.payload.token) as BLAdmin;

						return {
							...state,
							signInFailure: '',
							token: action.payload.token,
							user
						};
					} else {
						return {
							...state,
							token: null,
							user: null,
						};
					}
				},
			});
		}

		case SIGN_IN: {
			return handle(state, action, {
				start: (state) => ({
					...state,
					signinIn: true,
				}),
				finish: (state) => ({
					...state,
					signinIn: false,
				}),
				failure: (state) => ({
					...state,
					signInFailure: handleLoginErrorMessage(action.payload.message)
				}),
				success: (state) => {
					if (action.payload?.token) {
						const user = setStorageAndGetUser(action.payload.token) as BLAdmin;

						return {
							...state,
							signInFailure: '',
							token: action.payload.token,
							user
						};
					} else {
						if (
							action.payload.two_factor_required &&
							action.payload.temp_profile
						) {
							return {
								...state,
								temp_profile: action.payload.temp_profile,
								phone_hash: action.payload.phone_hash,
								request_phone: action.payload.request_phone,
								awaitCode: !action.payload.request_phone,
								signInFailure: ''
							};
						}

						return {
							...state,
							signInFailure: action.payload.message,
						};
					}
				},
			});
		}

		case SET_TEMP_NUMBER: {
			return handle(state, action, {
				start: (state) => ({
					...state,

				}),
				finish: (state) => ({
					...state,

				}),
				failure: (state) => ({
					...state,

				}),
				success: (state) => ({
					...state,
					phone_hash: action.payload.phone_hash,
					awaitCode: true
				}),
			});
		}

		case CONFIRM_CODE: {
			return handle(state, action, {
				start: (state) => ({
					...state,
					signinIn: true,
				}),
				finish: (state) => ({
					...state,
					signinIn: false,
				}),
				failure: (state) => ({
					...state,
					signInFailure: action.payload.message,
				}),
				success: (state) => {
					if (action.payload?.token) {
						//remember device for 30 days
						setStorageItem(StoredTokens.storedTwoFactor, action.payload.storage_token, 30 * 24);
						const user = setStorageAndGetUser(action.payload.token) as BLAdmin;
						return {
							...state,
							signInFailure: '',
							token: action.payload.token,
							user: user,
						};
					} else {
						return {
							...state,
							signInFailure: 'failed to confirm code'
						};
					}
				},
			});
		}

		case FORGOT_PASSWORD: {
			return handle(state, action, {
				start: (state) => ({
					...state,
					forgotPasswordLoading: true,
				}),
				finish: (state) => ({
					...state,
					forgotPasswordLoading: false,
				}),
				failure: (state) => ({
					...state,
					forgotPasswordError:
						'Unable to send password reset. Please check your inputs and try again.',
				}),
				success: (state) => ({
					...state,
					forgotPasswordSuccess: true,
					forgotPasswordError: '',
				}),
			});
		}

		case UPDATE_PASSWORD: {
			return handle(state, action, {
				start: (state) => ({
					...state,
					updatePasswordLoading: true,
				}),
				finish: (state) => ({
					...state,
					updatePasswordLoading: false,
				}),
				failure: (state) => ({
					...state,
					updatePasswordError:
						'Unable to send password update. Please check your inputs and try again.',
				}),
				success: (state) => ({
					...state,
					updatePasswordSuccess: true,
					updatePasswordError: '',
				}),
			});
		}

		case GET_ADMIN_CHANNELS: {
			return handle(state, action, {
				success: state => {

					// const user = state.user;
					// const active_channel = user?.active_channel;

					const channels = action.payload;
					// if (channels.find((channel: Channel) => channel.channel === state?.user?.active_channel)) {
					// 	console.log("yes");
					// 	console.log(JSON.stringify(user?.channels));
					// } else {
					// 	console.log("no");
					// 	user!.active_channel = channels[0].channel;
					// 	console.log(JSON.stringify(user?.channels));
					// 	delete user?.channels[active_channel!];
					// 	console.log(JSON.stringify(user?.channels));
					// 	setStorageItem(StoredTokens.currentUser, user, 1);
					// }

					return {
						...state,
						channels: channels,
						// user: user
					};
				}
			});
		}

		case SET_ADMIN_CHANNEL: {
			return handle(state, action, {
				start: state => ({ ...state, changingChannels: true }),
				success: (state) => {
					const user = setStorageAndGetUser(action.payload.token) as BLAdmin;

					return {
						...state,
						token: action.payload.token,
						user
					};
				},
				finish: state => ({ ...state, changingChannels: false })
			});
		}

		case UPDATE_CHANNEL: {
			return handle(state, action, {
				success: state => {
					return {
						...state,
						channels: state.channels.map((channel: Channel) => {
							return channel.channel === action.payload.channel ? action.payload : channel;
						}),
						updateChannelError: false,
					};
				},
				failure: state => {
					return {
						...state,
						updateChannelError: true,
					};
				}
			});
		}

		case SIGN_OUT: {
			removeStorageItem(StoredTokens.currentUser);

			return {
				...state,
				user: null,
				token: null,
			};
		}

		case ADD_CHECKLIST_ITEM: {
			return handle(state, action, {
				start: (state) => ({
					...state,
					onboardedLoading: true
				}),
				finish: (state) => ({
					...state,
					onboardedLoading: false
				}),
				success: (state) => {
					if (action.payload.error || !state.user)
						return state;
					else {
						return ({
							...state,
							user: {
								...state.user,
								onboarded: action.payload
							}
						});
					}
				}
			});
		}

		case CLEAR_CHECKLIST: {
			if (action.payload && state.user)
				return ({ ...state, user: { ...state.user, onboarded: [] } });
			else
				return state;
		}

		case COMPLETE_WELCOME_ONBOARDING: {
			return handle(state, action, {
				start: (state) => ({
					...state,
					onboardedLoading: true
				}),
				finish: (state) => ({
					...state,
					onboardedLoading: false
				}),
				success: (state) => {
					if (action.payload.error || !state.user)
						return state;
					else {
						return ({
							...state,
							user: {
								...state.user,
								onboarded: action.payload
							}
						});
					}
				}
			});
		}

		case UPDATE_ADMIN_PROFILE: {
			if (!state.user || !action.payload) return state;
			return {
				...state,
				user: {
					...state.user,
					profile: action.payload
				}
			};
		}

		default:
			return state;
	}
}
